import { useEffect, useRef } from "react";
import type { AuthContextProps } from "react-oidc-context";

import {
  enableOidcLogging,
  recordDiag,
  renewRetryDelayMs,
  shouldRenewSoon,
} from "./authDiagnostics";

const MAX_RETRIES = 3;

/**
 * Сторож обновления OIDC-сессии. Подключается один раз из App.
 *
 * Что делает поверх `automaticSilentRenew`:
 *  - логирует жизненный цикл токена и РЕАЛЬНУЮ причину провала renew (буфер +
 *    консоль) — чтобы «рандомный» вылет ловился без сидения в DevTools;
 *  - на ошибке silent-renew / экспирации делает до MAX_RETRIES ретраев
 *    `signinSilent` с джиттером. Ретрай перечитывает юзера из localStorage,
 *    поэтому подхватывает refresh-токен, проротированный другой вкладкой —
 *    гасит главный класс «рандомных» вылетов (гонка ротации);
 *  - при возврате на вкладку (focus/visible), если токен истёк/вот-вот —
 *    догоняет renew: фоновые таймеры душатся браузером и штатный renew мог не
 *    успеть до экспирации.
 *
 * `signinSilent` сериализуется флагом inFlight, чтобы не плодить параллельные
 * обновления (которые сами провоцируют гонку ротации).
 */
export function useAuthWatchdog(auth: AuthContextProps): void {
  const authRef = useRef(auth);
  authRef.current = auth;

  useEffect(() => {
    const events = authRef.current?.events;
    if (!events) return;

    enableOidcLogging();

    let inFlight = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const clearRetry = () => {
      if (retryTimer !== undefined) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }
    };

    function scheduleRetry(cause: string): void {
      if (attempt >= MAX_RETRIES) {
        recordDiag("renew-giveup", `${cause} after ${attempt} attempts`);
        return;
      }
      const delay = renewRetryDelayMs(attempt);
      attempt += 1;
      clearRetry();
      retryTimer = setTimeout(() => {
        void tryRenew(`retry#${attempt}`);
      }, delay);
      recordDiag("renew-retry", `#${attempt} in ${delay}ms (${cause})`);
    }

    async function tryRenew(cause: string): Promise<void> {
      const current = authRef.current;
      if (inFlight) {
        recordDiag("renew-skip", `${cause} (in-flight)`);
        return;
      }
      inFlight = true;
      recordDiag("renew-start", cause);
      try {
        await current.signinSilent();
        attempt = 0;
        clearRetry();
        recordDiag("renew-ok", cause);
      } catch (err) {
        recordDiag(
          "renew-fail",
          `${cause}: ${err instanceof Error ? err.message : String(err)}`,
        );
        scheduleRetry(cause);
      } finally {
        inFlight = false;
      }
    }

    const onExpiring = () => recordDiag("token-expiring");
    const onExpired = () => {
      recordDiag("token-expired");
      void tryRenew("expired");
    };
    const onSilentError = (err: Error) => {
      recordDiag("silent-renew-error", err?.message ?? String(err));
      scheduleRetry("silent-renew-error");
    };
    const onUserLoaded = () => {
      attempt = 0;
      clearRetry();
      recordDiag("user-loaded");
    };
    const onUserUnloaded = () => recordDiag("user-unloaded");

    events.addAccessTokenExpiring(onExpiring);
    events.addAccessTokenExpired(onExpired);
    events.addSilentRenewError(onSilentError);
    events.addUserLoaded(onUserLoaded);
    events.addUserUnloaded(onUserUnloaded);

    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible")
        return;
      const current = authRef.current;
      const nowSec = Math.floor(Date.now() / 1000);
      if (
        current.isAuthenticated &&
        shouldRenewSoon(current.user?.expires_at, nowSec)
      ) {
        void tryRenew("visible");
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      events.removeAccessTokenExpiring(onExpiring);
      events.removeAccessTokenExpired(onExpired);
      events.removeSilentRenewError(onSilentError);
      events.removeUserLoaded(onUserLoaded);
      events.removeUserUnloaded(onUserUnloaded);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearRetry();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
